import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError, AuthAPI, setToken } from "@/lib/api";
import GoldenParticles from "@/components/GoldenParticles";
import { Lock, ArrowLeft } from "lucide-react";

const EMERGENT_AUTH_URL = "https://auth.emergentagent.com/";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const redirect = params.get("redirect") || "/admin";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  // Retour de Google Sign-in : #session_id=... dans l'URL
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash || !hash.includes("session_id=")) return;
    const sid = new URLSearchParams(hash.slice(1)).get("session_id");
    navigate(window.location.pathname + window.location.search, { replace: true });
    if (!sid) return;
    setGoogleLoading(true);
    AuthAPI.googleExchange(sid)
      .then((data) => {
        setToken(data.token);
        const fallback =
          data.user?.role === "coach" ? "/coach" : data.user?.role === "organizer" ? "/organizer" : "/admin";
        window.location.replace(params.get("redirect") || fallback);
      })
      .catch((err) => {
        setError(formatApiError(err, "Connexion Google impossible"));
        setGoogleLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const googleSignIn = () => {
    const cb = `${window.location.origin}/login${
      params.get("redirect") ? `?redirect=${encodeURIComponent(params.get("redirect"))}` : ""
    }`;
    window.location.href = `${EMERGENT_AUTH_URL}?redirect=${encodeURIComponent(cb)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { user } = await login(email.trim(), password);
      // Coaches land on /coach; admins keep their requested redirect
      const fallback =
        user?.role === "coach" ? "/coach" : user?.role === "organizer" ? "/organizer" : "/admin";
      const target = params.get("redirect") || fallback;
      navigate(target, { replace: true });
    } catch (err) {
      setError(formatApiError(err, "Connexion impossible"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white relative overflow-hidden">
      {/* Atmospheric background */}
      <div
        className="absolute -top-[300px] left-1/2 -translate-x-1/2 w-[1000px] h-[1000px] rounded-full blur-3xl opacity-15 pointer-events-none"
        style={{ background: "#D4AF37" }}
      />
      <GoldenParticles count={12} density="subtle" />
      <div className="absolute inset-0 grain pointer-events-none opacity-40" />

      <div className="relative z-10 max-w-md mx-auto px-5 pt-20 pb-12">
        <Link
          to="/"
          data-testid="login-back-home"
          className="inline-flex items-center gap-2 text-zinc-500 hover:text-white transition-colors text-xs uppercase tracking-[0.25em] font-body mb-10"
        >
          <ArrowLeft className="w-3 h-3" /> Retour
        </Link>

        <div className="flex flex-col items-center text-center mb-8">
          <img
            src="/laser-tech-logo.png"
            alt="Laser Tech Création"
            className="h-20 mb-3"
            style={{ mixBlendMode: "screen" }}
          />
          <div
            className="text-[10px] font-bold uppercase tracking-[0.3em] mb-2"
            style={{ color: "#D4AF37" }}
          >
            Laser Tech Création
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tight">
            Espace <span style={{ color: "#D4AF37" }}>Admin</span>
          </h1>
          <p className="text-zinc-400 font-body text-sm mt-3 max-w-sm">
            Connecte-toi pour gérer les joueurs, les tournois et les pages
            connectées.
          </p>
        </div>

        <motion.form
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          onSubmit={submit}
          data-testid="login-form"
          className="bg-[#111216] border border-white/10 rounded-2xl p-6 md:p-8 space-y-5"
        >
          <div className="space-y-1.5">
            <Label htmlFor="email" className="text-overline !text-zinc-400">
              Email
            </Label>
            <Input
              id="email"
              data-testid="login-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="bg-[#0E0F13] border-white/10 text-white placeholder:text-zinc-600 focus-visible:border-[#D4AF37] focus-visible:ring-1 focus-visible:ring-[#D4AF37] rounded-md h-11"
              placeholder="info@lasertechcreation.com"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="password" className="text-overline !text-zinc-400">
              Mot de passe
            </Label>
            <Input
              id="password"
              data-testid="login-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="bg-[#0E0F13] border-white/10 text-white placeholder:text-zinc-600 focus-visible:border-[#D4AF37] focus-visible:ring-1 focus-visible:ring-[#D4AF37] rounded-md h-11"
              placeholder="••••••••"
            />
          </div>

          {error && (
            <div
              data-testid="login-error"
              className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2"
            >
              {error}
            </div>
          )}

          <Button
            type="submit"
            data-testid="login-submit"
            disabled={loading}
            className="w-full h-12 rounded-md text-black font-bold uppercase tracking-[0.18em] text-sm hover:opacity-90"
            style={{
              background:
                "linear-gradient(180deg, #FFE9A8 0%, #D4AF37 60%, #B8941F 100%)",
              boxShadow: "0 8px 30px rgba(212,175,55,0.2)",
            }}
          >
            <Lock className="w-4 h-4 mr-2" />
            {loading ? "Connexion..." : "Se connecter"}
          </Button>

          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-white/10" />
            <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-600 font-body">ou</span>
            <div className="h-px flex-1 bg-white/10" />
          </div>

          <Button
            type="button"
            data-testid="google-login-btn"
            disabled={googleLoading}
            onClick={googleSignIn}
            className="w-full h-12 rounded-md bg-white text-black font-bold uppercase tracking-[0.14em] text-sm hover:bg-zinc-200"
          >
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47a5.57 5.57 0 0 1-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z" />
              <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09A11.99 11.99 0 0 0 12 24z" />
              <path fill="#FBBC05" d="M5.27 14.29A7.16 7.16 0 0 1 4.9 12c0-.8.14-1.57.37-2.29V6.62H1.29a12 12 0 0 0 0 10.76l3.98-3.09z" />
              <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0A11.99 11.99 0 0 0 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z" />
            </svg>
            {googleLoading ? "Vérification..." : "Continuer avec Google"}
          </Button>
          <p className="text-[10px] text-zinc-600 font-body text-center leading-relaxed">
            Réservé aux comptes existants (admin, coach, organisateur) — l'email Google doit
            correspondre à ton compte Laser Tech.
          </p>
        </motion.form>

        <p className="text-center text-[10px] uppercase tracking-[0.3em] text-zinc-700 font-body mt-10">
          Laser Tech · Création · Genève
        </p>
      </div>
    </div>
  );
}
